import React, { useRef, useEffect, useState } from 'react';
import { Space, message, Button } from 'antd';
import { ClearOutlined, CheckOutlined } from '@ant-design/icons';
import SignaturePad from 'react-signature-canvas';
import './SignatureCanvas.css';

/**
 * 手写签名板组件
 *
 * 功能：
 * - 提供手写签名区域，支持鼠标和触屏
 * - 支持清空、确认操作
 * - 将签名转换为Base64图片数据
 * - 支持移动端和桌面端
 *
 * 使用示例：
 * <SignatureCanvas
 *   onConfirm={(dataUrl) => console.log(dataUrl)}
 *   onCancel={() => {}}
 * />
 */
const SignatureCanvas = ({ onConfirm, onCancel }) => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // 初始化画布尺寸
  useEffect(() => {
    if (wrapperRef.current) {
      const width = wrapperRef.current.offsetWidth;
      setDimensions({ width, height: 240 });
    }
  }, []);

  // 窗口大小变化时更新画布尺寸
  useEffect(() => {
    const handleResize = () => {
      if (wrapperRef.current) {
        const width = wrapperRef.current.offsetWidth;
        setDimensions({ width, height: 240 });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 画布就绪后清空
  useEffect(() => {
    if (canvasRef.current && dimensions.width > 0) {
      // 延迟清空确保画布已完全渲染
      const timer = setTimeout(() => {
        if (canvasRef.current) {
          canvasRef.current.clear();
          setIsEmpty(true);
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [dimensions]);

  // 处理签名开始
  const handleBegin = () => {
    setIsEmpty(false);
  };

  // 处理签名结束
  const handleEnd = () => {
    // 签名结束后检查是否为空
    if (canvasRef.current) {
      const isEmpty = canvasRef.current.isEmpty();
      setIsEmpty(isEmpty);
    }
  };

  // 清空画布
  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
      setIsEmpty(true);
    }
  };

  // 确认签名
  const handleConfirm = () => {
    if (!canvasRef.current) {
      message.warning('签名组件未就绪');
      return;
    }

    // 检查是否为空
    if (canvasRef.current.isEmpty()) {
      message.warning('请先签名');
      return;
    }

    try {
      // 获取签名的Base64数据（包含 data:image/png;base64, 前缀）
      const dataUrl = canvasRef.current.toDataURL('image/png');

      if (!dataUrl || dataUrl === 'data:,') {
        message.warning('签名数据无效，请重新签名');
        return;
      }

      if (onConfirm) {
        onConfirm(dataUrl);
      }
    } catch (e) {
      message.error('获取签名数据失败');
      console.error('Signature error:', e);
    }
  };

  // 取消操作
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // 如果尺寸还未计算，显示加载状态
  if (dimensions.width === 0) {
    return (
      <div className="signature-canvas-container">
        <div className="signature-canvas-wrapper" ref={wrapperRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          加载中...
        </div>
      </div>
    );
  }

  return (
    <div className="signature-canvas-container">
      {/* 签名画布 */}
      <div className="signature-canvas-wrapper" ref={wrapperRef}>
        <SignaturePad
          ref={canvasRef}
          canvasProps={{
            width: dimensions.width,
            height: dimensions.height,
            className: 'signature-canvas',
          }}
          penColor="black"
          minWidth={1.5}
          maxWidth={3.5}
          onBegin={handleBegin}
          onEnd={handleEnd}
          clearOnResize={false}
        />
      </div>

      {/* 操作按钮 */}
      <Space className="signature-actions">
        <Button icon={<ClearOutlined />} onClick={handleClear} size="large">
          清空
        </Button>
        <Button icon={<CheckOutlined />} type="primary" onClick={handleConfirm} size="large">
          确认
        </Button>
        <Button onClick={handleCancel} size="large">
          取消
        </Button>
      </Space>
    </div>
  );
};

export default SignatureCanvas;
